import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message, AttachedFile } from '../types';

interface MessageBubbleProps {
  message: Message;
  onTabChange?: (tab: 'you' | 'files') => void;
}

// File icon based on file type
function getFileIcon(fileType: string): string {
  if (fileType.includes('pdf')) return '📄';
  if (fileType.includes('image')) return '🖼️';
  if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType.includes('csv')) return '📊';
  if (fileType.includes('dwg') || fileType.includes('dxf')) return '📐';
  return '📎';
}

// Format file size
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// File chip component
function FileChip({ file }: { file: AttachedFile }) {
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-800 rounded-full text-xs max-w-[200px]">
      <span>{getFileIcon(file.file_type)}</span>
      <span className="truncate font-medium">{file.filename}</span>
      <span className="text-blue-600 flex-shrink-0">{formatFileSize(file.file_size)}</span>
    </div>
  );
}

export function MessageBubble({ message, onTabChange }: MessageBubbleProps) {
  // System messages (special bubbles)
  if (message.role === 'system' && message.type && message.metadata) {
    return <SystemMessageBubble message={message} onTabChange={onTabChange} />;
  }

  // Regular user/assistant messages
  const isUser = message.role === 'user';
  const hasFiles = message.files && message.files.length > 0;

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} mb-4`}>
      {/* File chips above message bubble (user messages only) */}
      {isUser && hasFiles && (
        <div className="flex flex-wrap gap-1.5 mb-1.5 max-w-[80%] justify-end">
          {message.files!.map((file) => (
            <FileChip key={file.file_id} file={file} />
          ))}
        </div>
      )}

      {/* Message bubble */}
      <div
        className={`max-w-[80%] rounded-lg px-4 py-2 ${
          isUser
            ? 'bg-primary text-white'
            : 'bg-gray-100 text-gray-900'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-2 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-strong:text-gray-900">
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
          </div>
        )}
        {message.images?.map((src, index) => (
          <img
            key={index}
            src={src}
            alt={`Attachment ${index + 1}`}
            className="mt-2 max-w-full rounded"
          />
        ))}
      </div>
    </div>
  );
}

function SystemMessageBubble({ message, onTabChange }: MessageBubbleProps) {
  const { type, metadata } = message;

  // Color schemes based on type
  const colors = {
    file_upload: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-gray-700',
      link: 'text-blue-700',
      emoji: '📎',
    },
    contact_prompt: {
      bg: 'bg-amber-50',
      border: 'border-amber-300',
      text: 'text-amber-900',
      link: 'text-amber-700',
      emoji: '💡',
    },
    research_prompt: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-900',
      link: 'text-purple-700',
      emoji: '🔬',
    },
    handover_prompt: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-900',
      link: 'text-green-700',
      emoji: '👤',
    },
  };

  const scheme = type ? colors[type] : colors.file_upload;

  const handleActionClick = () => {
    if (metadata?.tabTarget && onTabChange) {
      onTabChange(metadata.tabTarget);
    }
  };

  return (
    <div className="flex justify-center mb-4 px-4">
      <div className={`max-w-[60%] ${scheme.bg} border-2 ${scheme.border} rounded-lg px-4 py-2.5 text-center`}>
        <div className="text-lg mb-1">{scheme.emoji}</div>

        {type === 'file_upload' && metadata?.filename && (
          <div className={`text-sm ${scheme.text}`}>
            <div className="font-medium">{metadata.filename}</div>
            <div className="text-xs opacity-75">
              {metadata.size && `${(metadata.size / 1024).toFixed(1)} KB`}
              {message.timestamp && ` • ${new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
            </div>
          </div>
        )}

        {(type === 'contact_prompt' || type === 'research_prompt') && metadata?.message && (
          <>
            <p className={`text-sm ${scheme.text} mb-2`}>{metadata.message}</p>
            {metadata.actionLabel && metadata.tabTarget && (
              <button
                onClick={handleActionClick}
                className={`text-xs ${scheme.link} underline cursor-pointer hover:opacity-80`}
              >
                {metadata.actionLabel} →
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
